import { Publish } from "@mui/icons-material";
import {
  Button,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState,useRef } from "react";
import { useWatch } from "react-hook-form";
import {
  FormAutoComplete,
  FormSelectInput,
  FormTextInput,
} from "../../../components/form";
import { useInterventionsContext } from "../../../contexts/InterventionsContext";
import { GetMedication } from "../../../services/terminology";
import { CustomMedicationOption } from "./CustomOptions/CustomMedicationOption";
import { GetMedicationINT } from "@/services/terminology/GetMedicationINT";
import {
  RECEITUARIO_TIPO_SELECT_AUTO,
  RECEITUARIO_TIPO_SELECT_OPTIONS,
  resolveReceituarioProfile,
} from "src-next/modules/sncr";

const unit = [
  {
    value: "GRAN",
    label: "Grânulos",
    inputType: "number",
  },
  {
    value: "COM",
    label: "Comprimido",
    inputType: "number",
  },
  {
    value: "CAP",
    label: "Cápsula",
    inputType: "number",
  },
  {
    value: "SOL OR",
    label: "Solução Oral",
  },
  {
    value: "SOL INJ",
    label: "Solução Injetável",
  },
  {
    value: "SOL",
    label: "Solução",
  },

  {
    value: "SUSP",
    label: "Suspensão",
  },
  {
    value: "POM",
    label: "Pomada",
  },
  {
    value: "GEL",
    label: "Gel",
  },
  {
    value: "XAR",
    label: "Xarope",
  },
  {
    value: "INJ",
    label: "Injetável",
  },
  {
    value: "COL",
    label: "Colírio",
  },
  {
    value: "AER",
    label: "Aerossol",
  },
  {
    value: "SUP",
    label: "Supositório",
    inputType: "number",
  },
  {
    value: "AMP",
    label: "Ampola",
    inputType: "number",
  },
  {
    value: "CTG",
    label: "Cartucho",
    inputType: "number",
  },
  {
    value: "FLD",
    label: "Fluido",
  },
  {
    value: "CJ  ",
    label: "Conjunto",
    inputType: "number",
  },
  {
    value: "ENV",
    label: "Envelope ou Sachê",
  },
  {
    value: "BISN",
    label: "Bisnaga",
    inputType: "number",
  },
  {
    value: "DISP",
    label: "Dispositivo",
  },
  {
    value: "BALA",
    label: "Bala",
    inputType: "number",
  },
  {
    value: "LAQ",
    label: "Laca",
  },
];

const frequency = [
  { value: 3, label: "3 h" },
  { value: 6, label: "6 h" },
  { value: 8, label: "8 h" },
  { value: 12, label: "12 h" },
  { value: 24, label: "24 h" },
];

const time = [
  { value: "", label: "tempo indeterminado" },
  { value: 1, label: "1 dia" },
  { value: 2, label: "2 dias" },
  { value: 3, label: "3 dias" },
  { value: 5, label: "5 dias" },
  { value: 7, label: "7 dias" },
  { value: 15, label: "15 dias" },
];

export function MedicationFields({ control, name, setValue, render }: any) {
  const { medicationOptions, setMedication } = useInterventionsContext();
  const [type, setType] = useState(1);

  function verityInputType() {
    const option = unit.find((el: any) => el.value === selectedForm?.value);
    return option?.inputType || "text";
  }

  const frequencyDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  function extrairNumero(string: string) {
    const match = string?.match(/\d+/);
    return match ? Number(match[0]) : null;
  }

  function queueTiming(
    timer: { current: ReturnType<typeof setTimeout> | null },
    text: string,
    field: "frequency" | "time",
    labelOf: (amount: number) => string,
    delayMs: number,
  ) {
    if (timer.current) clearTimeout(timer.current);
    const typed = text?.trim() ?? "";
    // Texto vazio ou sem número (ex.: "tempo indeterminado") não apaga a opção já escolhida.
    if (!typed) return;
    const amount = extrairNumero(typed);
    if (amount === null) return;
    // Espera a última tecla para não trocar "1" por "1 dia" no meio de "15".
    timer.current = setTimeout(() => {
      const label = labelOf(amount);
      setValue(field, { name: label, label, value: amount });
    }, delayMs);
  }

  useEffect(() => {
    return () => {
      if (frequencyDebounce.current) clearTimeout(frequencyDebounce.current);
      if (timeDebounce.current) clearTimeout(timeDebounce.current);
    };
  }, []);

  let selectedMedication = useWatch({ name: "medication", control });
  let selectedForm = useWatch({ name: "form", control });
  const receituarioTipo = useWatch({ name: "receituarioTipo", control });

  const [customMedication, setCustomMedication] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const autoReceituario = useMemo(
    () =>
      resolveReceituarioProfile({
        tag: selectedMedication?.tag,
        class: selectedMedication?.class,
        name: selectedMedication?.name,
        label: selectedMedication?.label,
      }),
    [
      selectedMedication?.tag,
      selectedMedication?.class,
      selectedMedication?.name,
      selectedMedication?.label,
    ],
  );

  useEffect(() => {
    if (receituarioTipo == null || receituarioTipo === "") {
      setValue("receituarioTipo", RECEITUARIO_TIPO_SELECT_AUTO);
    }
  }, [receituarioTipo, setValue]);

  useEffect(() => {
    async function fetchMedication() {
      const medicationResponse =
        name !== "fitoterápico"
          ? await GetMedication(type?.toString(), selectedMedication?.name)
          : await GetMedicationINT(type?.toString(), selectedMedication?.name);
      setMedication(
        medicationResponse?.data?.entry?.map((el: any) => el.resource)
      );
      setLoading(false);
    }

    if (selectedMedication?.code) setCustomMedication(false);

    // value numérico = escolha no catálogo. Item da reemissão não tem esse value;
    // não substituir a forma da posologia por um token do nome comercial (ex.: COM).
    const pickedFromCatalog = typeof selectedMedication?.value === "number";
    const presentationForm = unit?.find((el: any) =>
      selectedMedication?.label?.includes(el.value)
    );
    if (presentationForm && !customMedication && pickedFromCatalog) {
      setValue("form", presentationForm);
    }

    const debounce = setTimeout(() => {
      if (selectedMedication?.name?.length) {
        setLoading(true);
        fetchMedication();
      }
    }, 1500);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMedication, setValue]);

  return (
    <Grid
      container
      direction="row"
      justifyContent="center"
      alignItems="center"
      spacing={2}
    >
      {name === "medicamento" && (
        <Grid item xs={12}>
          <Stack
            direction="row"
            alignItems="flex-end"
            justifyContent="space-between"
          >
            <Stack>
              <FormLabel component="legend"> Buscar por: </FormLabel>
              <RadioGroup
                row
                aria-labelledby="demo-controlled-radio-buttons-group"
                aria-label="position"
                sx={{ width: "100%" }}
                defaultValue={1}
                // value={field?.value ?? ''}
              >
                <FormControlLabel
                  key={1}
                  value={1}
                  control={<Radio />}
                  onChange={() => setType(1)}
                  label="Nome comercial"
                />
                <FormControlLabel
                  key={2}
                  value={2}
                  control={<Radio />}
                  onChange={() => setType(2)}
                  label="Princípio ativo"
                />
                <FormControlLabel
                  key={3}
                  value={3}
                  control={<Radio />}
                  onChange={() => setType(3)}
                  label="Nome genérico"
                />
              </RadioGroup>
            </Stack>

            <Stack sx={{ minWidth: 260, maxWidth: 320 }}>
              <FormSelectInput
                name="receituarioTipo"
                control={control}
                label="Tipo de receita"
                selectOptions={RECEITUARIO_TIPO_SELECT_OPTIONS}
                defaultValue={RECEITUARIO_TIPO_SELECT_AUTO}
              />
              {(!receituarioTipo ||
                receituarioTipo === RECEITUARIO_TIPO_SELECT_AUTO) && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Automático: {autoReceituario.label}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Grid>
      )}

      {name === "fitoterápico" && (
        <Grid item xs={12}>
          <Stack
            direction="row"
            alignItems="flex-end"
            justifyContent="space-between"
          >
            <Stack>
              <FormLabel component="legend"> Buscar por: </FormLabel>
              <RadioGroup
                row
                aria-labelledby="demo-controlled-radio-buttons-group"
                aria-label="position"
                sx={{ width: "100%" }}
                defaultValue={1}
                // value={field?.value ?? ''}
              >
                <FormControlLabel
                  key={1}
                  value={1}
                  control={<Radio />}
                  onChange={() => setType(1)}
                  label="Nome comercial"
                />
                <FormControlLabel
                  key={2}
                  value={2}
                  control={<Radio />}
                  onChange={() => setType(2)}
                  label="Princípio ativo"
                />
              </RadioGroup>
            </Stack>
          </Stack>
        </Grid>
      )}

      <Grid item xs={12}>
        {render && (
          <FormAutoComplete
            required
            loading={loading}
            name="medication"
            freeSolo={customMedication}
            control={control}
            onInputChange={(e: any) => {
              if (e?.target?.value?.length)
                setValue("medication", {
                  name: e?.target?.value,
                  label: e?.target?.value,
                  value: e?.target?.value,
                });
            }}
            label={`Selecione o ${name}`}
            renderOption={(option: any) => (
              <CustomMedicationOption option={option} />
            )}
            noOptionsText={
              <Button
                sx={{ width: "fit-content", textTransform: "none" }}
                onClick={() => setCustomMedication(true)}
                endIcon={<Publish />}
              >
                Adicionar novo medicamento
              </Button>
            }
            selectOptions={!loading ? medicationOptions : []}
            autoFocus
          />
        )}
      </Grid>

      <Grid item xs={3}>
        {render && (
          <FormTextInput
            type={verityInputType()}
            name="quantity"
            control={control}
            label="Posologia"
            pattern={/^\d+(\.\d+)?\s*\w*(\s+.*)?$/}
          />
        )}
      </Grid>
      <Grid item xs={3}>
        {render && (
          <FormAutoComplete
            onInputChange={(e: any) => {
              if (e?.target?.value?.length)
                setValue("form", {
                  name: e?.target?.value,
                  label: e?.target?.value,
                  value: e?.target?.value,
                });
            }}
            freeSolo
            name="form"
            control={control}
            label="Forma"
            selectOptions={unit}
          />
        )}
      </Grid>

      <Grid item xs={3}>
        {render && (
          <FormAutoComplete
            freeSolo
            onInputChange={(_event, text = "", reason) => {
              // "reset" dispara ao escolher a opção. Não pode apagar esse valor.
              if (reason === "reset") return;
              queueTiming(
                frequencyDebounce,
                text,
                "frequency",
                (amount) => `${amount} h`,
                300,
              );
            }}
            name="frequency"
            control={control}
            label="Intervalo (horas)"
            selectOptions={frequency}
          />
        )}
      </Grid>
      <Grid item xs={3}>
        {render && (
          <FormAutoComplete
            freeSolo
            onInputChange={(_event, text = "", reason) => {
              if (reason === "reset") return;
              queueTiming(
                timeDebounce,
                text,
                "time",
                (amount) => (amount === 1 ? "1 dia" : `${amount} dias`),
                800,
              );
            }}
            name="time"
            control={control}
            label="Tempo (dias)"
            selectOptions={time}
          />
        )}
      </Grid>
      {render && (
        <Grid item xs={12} height="auto">
          <FormTextInput
            multiline={true}
            minRows={3}
            label="Observação"
            name="note"
            control={control}
          />
        </Grid>
      )}
    </Grid>
  );
}
